// @refresh reset
import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { RealtimeChannel } from '@supabase/supabase-js';

const isDev = import.meta.env.DEV;

interface UpdateMaintenanceParams {
  enabled: boolean;
  message: string;
  updated_by: string;
}
interface MaintenanceChangePayload {
  new: {
    setting_value: string;
  };
}

interface MaintenanceStatus {
  enabled: boolean;
  message: string;
  allowed_roles: string[];
  updated_by?: string;
  updated_at?: string;
}

interface MaintenanceContextType {
  maintenanceStatus: MaintenanceStatus | null;
  isInMaintenance: boolean;
  canBypassMaintenance: boolean;
  updateMaintenanceMode: (enabled: boolean, message?: string) => Promise<void>;
  loading: boolean;
  refreshStatus: () => Promise<void>;
}

export const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

interface MaintenanceProviderProps {
  children: ReactNode;
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ children }) => {
  const { user, userRole } = useAuth();
  const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const mountedRef = useRef(true);
  const subscriptionSetupRef = useRef(false);
  const pollIntervalRef = useRef<number | null>(null);

  // Fetch maintenance status from database
  const fetchMaintenanceStatus = useCallback(async () => {
    if (!mountedRef.current) return;
    
    try {
      if (isDev) console.log('Fetching maintenance status...');
      const { data, error } = await supabase.rpc('get_maintenance_status');

      if (error) {
        if (isDev) console.error('Error fetching maintenance status:', error);
        return;
      }

      const parsedData = data ?
        (data as unknown as MaintenanceStatus) :
        { enabled: false, message: '', allowed_roles: ['admin'] };

      if (mountedRef.current) {
        setMaintenanceStatus(parsedData);
        if (isDev) console.log('Maintenance status updated:', parsedData);
      }
    } catch (error) {
      if (isDev) console.error('Unexpected error fetching maintenance status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update maintenance mode
  const updateMaintenanceMode = useCallback(async (enabled: boolean, message?: string) => {
    if (!user) {
      throw new Error('User must be authenticated to update maintenance mode.');
    }

    try {
      setLoading(true);
      
      const params = {
        enabled: enabled,
        message: message || 'System is currently under maintenance. Please check back later.'
      };

      const { error } = await supabase.rpc('update_maintenance_mode', params);

      if (error) throw error;

      // Refresh status after update
      await fetchMaintenanceStatus();
    } catch (error) {
      if (isDev) console.error('Error updating maintenance mode:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, fetchMaintenanceStatus]);

  // Manual refresh function
  const refreshStatus = useCallback(async () => {
    setLoading(true);
    await fetchMaintenanceStatus();
  }, [fetchMaintenanceStatus]);

  // Handle realtime maintenance updates
  const handleMaintenanceUpdate = useCallback((payload: MaintenanceChangePayload) => {
    if (!mountedRef.current) return;
    
    try {
      const newStatus = JSON.parse(payload.new.setting_value);
      if (isDev) console.log('Received maintenance status change:', newStatus);

      // Immediately update the status
      setMaintenanceStatus(newStatus);
    } catch (error) {
      if (isDev) console.error('Error parsing maintenance status update:', error);
    }
  }, []);

  // Setup realtime subscription with safe gating
  useEffect(() => {
    mountedRef.current = true;

    // Always fetch current status once
    fetchMaintenanceStatus();

    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Avoid opening WebSockets for anonymous/public visitors (prevents console errors in audits)
    const shouldSubscribe = typeof window !== 'undefined' && !!user;

    if (shouldSubscribe) {
      // Setup subscription only once
      if (!subscriptionSetupRef.current) {
        subscriptionSetupRef.current = true;
        if (isDev) console.log('Setting up maintenance subscription...');

        const channelName = 'maintenance-status-channel';
        const channel = supabase.channel(channelName);

        channel
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'system_settings',
              filter: 'setting_key=eq.maintenance_mode',
            },
            handleMaintenanceUpdate
          )
          .subscribe((status, err) => {
            if (isDev) console.log('Subscription status:', status);
            if (err) {
              // Downgrade to warn to avoid failing audits while keeping visibility
              if (isDev) console.warn('Subscription error (non-fatal):', err);
              return;
            }
            if (status === 'SUBSCRIBED') {
              if (isDev) console.log('Successfully subscribed to maintenance updates');
            }
          });

        channelRef.current = channel;
      }
    } else {
      // Lightweight polling fallback every 60s for public users
      if (typeof window !== 'undefined') {
        pollIntervalRef.current = window.setInterval(() => {
          fetchMaintenanceStatus();
        }, 60000);
      }
    }

    // Cleanup
    return () => {
      mountedRef.current = false;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      if (channelRef.current) {
        if (isDev) console.log('Cleaning up maintenance subscription...');
        channelRef.current.unsubscribe();
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        subscriptionSetupRef.current = false;
      }
    };
  }, [user, fetchMaintenanceStatus, handleMaintenanceUpdate]);

  // Computed values
  const isInMaintenance = maintenanceStatus?.enabled || false;
  const canBypassMaintenance = userRole === 'admin' && 
    maintenanceStatus?.allowed_roles?.includes('admin');

  const contextValue: MaintenanceContextType = {
    maintenanceStatus,
    isInMaintenance,
    canBypassMaintenance,
    updateMaintenanceMode,
    loading,
    refreshStatus
  };

  return (
    <MaintenanceContext.Provider value={contextValue}>
      {children}
    </MaintenanceContext.Provider>
  );
};

// Custom hook to use the maintenance context
export const useMaintenance = (): MaintenanceContextType => {
  const context = useContext(MaintenanceContext);
  if (context === undefined) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }
  return context;
};